import React, { ForwardedRef, ReactNode, CSSProperties } from 'react';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSelector } from 'react-redux';
import Icon from 'src/components/ui/Icon/Icon';
import { getDashboard } from 'src/redux/dashboard/selectors';
import classNames from 'classnames';
import CardTitle from 'src/components/ui/CardTitle/CardTitle';
import styles from './CardContainer.module.scss';

interface CardContainerProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  label?: string;
  nextArrow?: boolean;
  underlined?: boolean;
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  isRow?: boolean;
  disablePadding?: boolean;
  smallLabel?: boolean;
  actionChildren?: ReactNode;
}

const CardContainer = React.forwardRef<HTMLDivElement, CardContainerProps>(
  (
    {
      children,
      className,
      style,
      label,
      nextArrow,
      underlined,
      attributes,
      listeners,
      isRow,
      disablePadding,
      smallLabel,
      actionChildren,
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const dashboardState = useSelector(getDashboard);
    const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      const widgetElement = event.currentTarget.closest('[class*="Card"]');
      if (widgetElement) {
        widgetElement.remove();
      }
    };
    return (
      <div
        ref={ref}
        className={classNames(styles.root, `card-container surface-elevated `, className, { 'p-20': !disablePadding })}
        style={style}>
        {!isRow && (
          <div className="flex justify-between items-center pb-20">
            <div className="flex w-full">
              {dashboardState.isAdjusting && (
                <button type="button" aria-label="Adjust" className={styles.icon} {...attributes} {...listeners}>
                  <Icon name="dragIndicator" width={20} height={20} className="" />
                </button>
              )}
              {label && (
                <CardTitle smallLabel={smallLabel} label={label} hasNextArrow={nextArrow} isUnderlined={underlined} />
              )}
              {actionChildren && <div className="ml-auto">{actionChildren}</div>}
            </div>
            {dashboardState.isAdjusting && (
              <button type="button" aria-label="Delete" onClick={handleDeleteClick} className={styles.icon}>
                <Icon name="deleteWidget" width={20} height={20} className="" />
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    );
  },
);

export default CardContainer;
